import { KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';

import cx from 'classnames';
import { isEmpty, isNil } from 'ramda';

import s from './index.module.css';
import { IGlobalSearchInputProps } from './types';

export const useGlobalSearchInput = ({
  value,
  onSubmit,
  onClear,
}: Pick<IGlobalSearchInputProps, 'value' | 'onSubmit' | 'onClear'>) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [hasChangesSinceLastSubmit, setHasChangesSinceLastSubmit] = useState<boolean>(false);
  const isSearchEmpty = useMemo(() => isNil(value) || isEmpty(value), [value]);

  const clearIconClassNames = useMemo(
    () => cx(s.clear, isSearchEmpty && s.hidden, !isFocused && isSearchEmpty && s.zeroMargin),
    [isFocused, isSearchEmpty],
  );

  const submit = useCallback(() => {
    onSubmit();
    setHasChangesSinceLastSubmit(false);
  }, [onSubmit]);

  const handleEnterKey = useCallback(
    (evt: KeyboardEvent) => {
      if (evt.key === 'Enter') {
        evt.stopPropagation();
        evt.preventDefault();

        submit();
      }
    },
    [submit],
  );

  const handleClear = useCallback(
    /*@ts-ignore*/
    (evt) => {
      evt.stopPropagation();
      evt.preventDefault();

      onClear();
    },
    [onClear],
  );

  const handleSubmit = useCallback(
    /*@ts-ignore*/
    (evt) => {
      evt.stopPropagation();
      evt.preventDefault();

      submit();
    },
    [submit],
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);

    if (hasChangesSinceLastSubmit) {
      submit();
    }
  }, [hasChangesSinceLastSubmit, submit]);

  useEffect(() => {
    if (!hasChangesSinceLastSubmit) {
      setHasChangesSinceLastSubmit(true);
    }
    // eslint-disable-next-line
  }, [value]);

  useEffect(() => {
    if ((isEmpty(value) || isNil(value)) && hasChangesSinceLastSubmit && !isFocused) {
      submit();
    }
  }, [value, hasChangesSinceLastSubmit, submit, isFocused]);

  return {
    isSearchEmpty,
    isFocused,
    clearIconClassNames,
    handleEnterKey,
    handleSubmit,
    handleClear,
    handleFocus,
    handleBlur,
  };
};
